// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/install/InstallAppSheet.tsx
// 🔧 PURPOSE: Il foglio "Installa l'app", nello stile della Home (HomeSheet):
//   l'icona dell'app, tre buoni motivi e poi, secondo il telefono
//   (lib/installApp, installGuide):
//   - Android con Chrome pronto: "Installa" (la finestra del sistema);
//   - iPhone/iPad: i passi giusti (Safari 26 con "•••", Safari più vecchio,
//     iPad, Chrome per iPhone);
//   - dentro Instagram/Facebook/…: prima "Apri nel browser", con "Copia link";
//   - Android senza finestra pronta: i passi dal menu del browser.
//   In fondo "Non ora" e "L'ho già installata".
// ============================================

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCircle2, Copy, KeyRound, MoreHorizontal, PlusSquare, Share, Wifi } from 'lucide-react';
import HomeSheet from '../home/HomeSheet';
import { FOCUS_RING, INK_TEXT, MUTED_TEXT } from '../home/ui';
import { copyText } from '../../lib/clipboard';
import { promptInstall, type InstallGuide } from '../../lib/installApp';
import { cn } from '../../lib/utils';

export type InstallCloseReason = 'later' | 'never' | 'done';

interface InstallAppSheetProps {
  open: boolean;
  guide: InstallGuide;
  /** L'ospite è collegato: su iPhone l'app installata chiederà di nuovo l'accesso. */
  loggedIn?: boolean;
  onClose: (reason: InstallCloseReason) => void;
}

const BUTTON = cn(
  'inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl px-4 text-[13px] font-extrabold uppercase tracking-button',
  'bg-nva-espresso text-white dark:bg-stone-100 dark:text-stone-900 motion-safe:active:scale-[0.98] disabled:opacity-60',
  FOCUS_RING,
);

const Step: React.FC<{ n: number; icon?: React.ReactNode; children: React.ReactNode }> = ({ n, icon, children }) => (
  <li className={cn('flex items-start gap-3 text-[14.5px] leading-snug', INK_TEXT)}>
    <span
      aria-hidden="true"
      className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-nva-cream font-display text-[13px] font-bold text-nva-gold-text dark:bg-nva-cream-dark dark:text-nva-gold-dark"
    >
      {n}
    </span>
    <span className="min-w-0 flex-1 pt-0.5">{children}</span>
    {icon ? (
      <span aria-hidden="true" className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border border-nva-edge text-nva-espresso dark:border-stone-700 dark:text-stone-200 [&>svg]:h-4 [&>svg]:w-4">
        {icon}
      </span>
    ) : null}
  </li>
);

const InstallAppSheet: React.FC<InstallAppSheetProps> = ({ open, guide, loggedIn, onClose }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const install = async () => {
    setBusy(true);
    const outcome = await promptInstall();
    setBusy(false);
    if (outcome === 'accepted') onClose('done');
  };

  const copyLink = async () => {
    if (await copyText(window.location.href)) setCopied(true);
  };

  let steps: React.ReactNode = null;
  if (guide.kind === 'ios') {
    const first =
      guide.browser === 'chrome' ? (
        <Step n={1} icon={<Share />}>{t('installApp.ios.shareChrome')}</Step>
      ) : guide.browser === 'other' ? (
        <Step n={1} icon={<Share />}>{t('installApp.ios.shareOther')}</Step>
      ) : guide.ipad ? (
        <Step n={1} icon={<Share />}>{t('installApp.ios.shareTop')}</Step>
      ) : guide.modern ? (
        <>
          <Step n={1} icon={<MoreHorizontal />}>{t('installApp.ios.moreBottom')}</Step>
          <Step n={2} icon={<Share />}>{t('installApp.ios.tapShare')}</Step>
        </>
      ) : (
        <Step n={1} icon={<Share />}>{t('installApp.ios.shareBottom')}</Step>
      );
    const offset = guide.browser === 'safari' && guide.modern && !guide.ipad ? 2 : 1;
    steps = (
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        {first}
        <Step n={offset + 1} icon={<PlusSquare />}>{t('installApp.ios.addHome')}</Step>
        <Step n={offset + 2}>{guide.modern ? t('installApp.ios.confirmWebApp') : t('installApp.ios.confirm')}</Step>
      </ol>
    );
  } else if (guide.kind === 'android') {
    steps = (
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        <Step n={1}>{t(`installApp.android.${guide.browser}.step1`)}</Step>
        <Step n={2}>{t(`installApp.android.${guide.browser}.step2`)}</Step>
        <Step n={3}>{t('installApp.android.confirm')}</Step>
      </ol>
    );
  } else if (guide.kind === 'inapp') {
    steps = (
      <div className="flex flex-col gap-3">
        <p className={cn('m-0 text-[14.5px] leading-snug', INK_TEXT)}>{t('installApp.inapp.text')}</p>
        <ol className="m-0 flex list-none flex-col gap-3 p-0">
          <Step n={1} icon={<MoreHorizontal />}>{t(guide.ios ? 'installApp.inapp.stepIos' : 'installApp.inapp.stepAndroid')}</Step>
          <Step n={2}>{t('installApp.inapp.thenInstall')}</Step>
        </ol>
        <button type="button" onClick={copyLink} className={cn(BUTTON, 'bg-transparent text-nva-espresso border border-nva-espresso/25 dark:bg-transparent dark:text-stone-100 dark:border-stone-600')}>
          {copied ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
          {copied ? t('installApp.inapp.copied') : t('installApp.inapp.copy')}
        </button>
      </div>
    );
  }

  return (
    <HomeSheet
      open={open}
      onClose={() => onClose('later')}
      title={t('installApp.title')}
      eyebrow={t('installApp.eyebrow')}
      titleId="install-app-title"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <img src="/icons/icon-192x192.png" alt="" width={56} height={56} className="h-14 w-14 flex-shrink-0 rounded-[14px] shadow-sm" />
          <div className="min-w-0">
            <p className={cn('m-0 text-[15.5px] font-bold leading-tight', INK_TEXT)}>Nonna Vittoria Apartments</p>
            <p className={cn('m-0 mt-0.5 text-[13px]', MUTED_TEXT)}>{t('installApp.appSub')}</p>
          </div>
        </div>

        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {[
            { icon: <Bell />, key: 'alerts' },
            { icon: <KeyRound />, key: 'door' },
            { icon: <Wifi />, key: 'fast' },
          ].map((b) => (
            <li key={b.key} className={cn('flex items-center gap-2.5 text-[14px] leading-snug', INK_TEXT)}>
              <span aria-hidden="true" className="text-nva-gold [&>svg]:h-[18px] [&>svg]:w-[18px]">{b.icon}</span>
              {t(`installApp.benefits.${b.key}`)}
            </li>
          ))}
        </ul>

        {guide.kind === 'native' ? (
          <button type="button" onClick={install} disabled={busy} className={BUTTON}>
            {t('installApp.install')}
          </button>
        ) : steps ? (
          <div className="flex flex-col gap-3 rounded-2xl border border-nva-edge bg-nva-cream/60 p-3.5 dark:border-stone-800 dark:bg-stone-900">
            <p className="m-0 text-[11px] font-extrabold uppercase tracking-[0.18em] text-nva-gold-text dark:text-nva-gold-dark">
              {t('installApp.howTo')}
            </p>
            {steps}
          </div>
        ) : null}

        {guide.kind === 'ios' && loggedIn ? (
          <p className={cn('m-0 text-[12.5px] leading-snug', MUTED_TEXT)}>{t('installApp.ios.loginAgain')}</p>
        ) : null}

        <div className="flex items-center justify-between gap-3 pt-1">
          <button type="button" onClick={() => onClose('never')} className={cn('min-h-[44px] rounded px-1 text-[13px] font-bold underline underline-offset-2', MUTED_TEXT, FOCUS_RING)}>
            {t('installApp.already')}
          </button>
          <button type="button" onClick={() => onClose('later')} className={cn('min-h-[44px] rounded-xl px-4 text-[13px] font-extrabold uppercase tracking-button', INK_TEXT, FOCUS_RING)}>
            {t('installApp.later')}
          </button>
        </div>
      </div>
    </HomeSheet>
  );
};

export default InstallAppSheet;
